import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import styles from './PdfRenderer.module.scss';
import * as pdfjsLib from 'pdfjs-dist';
import { serializeSelection } from './helpers/textOffset';
import { renderHighlight } from './helpers/highlight';
import { toNormalized } from './helpers/coords';
import { NotePopup } from './components/NotePopup';
import { AnnotationsSidebar } from './components/AnnotationsSidebar';
import type { Highlight, HighlightGeometry, HighlightMoveHandler, ViewerMode, ContractorNotePermissions } from './types';
import type { CommentsPanelProps } from './components/AnnotationsSidebar';
import type { ModelAction } from '../../../../types/responses/ModelAction';
import { PDFNotesActionsVals } from '../../../../helpers/mapping/PDFNotesActionsVals';
import { isOwnHighlight } from './helpers/isOwnHighlight';
import { keepUnresolvedFailedHighlights } from './helpers/failedHighlights';
import { usePdfZoom } from './hooks/usePdfZoom';
import { usePdfPan } from './hooks/usePdfPan';
import { useSidebarResize } from './hooks/useSidebarResize';
import { usePopupManager } from './hooks/usePopupManager';
import { useHighlightActions } from './hooks/useHighlightActions';
import { useViewerModes } from './hooks/useViewerModes';
import { PdfToolbar } from './components/PdfToolbar';
import { PendingAnnotationModals } from './components/PendingAnnotationModals';
import { SvgAnnotationLayer } from './components/SvgAnnotationLayer';
import { PositionedMarkers } from './components/PositionedMarkers';
import { useNoteCodes } from './hooks/useNoteCodes';
import { usePdfThumbnails } from './hooks/usePdfThumbnails';
import { ThumbnailsSidebar } from './components/ThumbnailsSidebar';
import { DocumentSearchBar } from './components/DocumentSearchBar';
import { usePdfViewerSearch } from './hooks/usePdfViewerSearch';

const DEFAULT_WORKER_SRC = '/pdf.worker.js';

const MAX_BASE = 1.5;
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 3;

interface ActivePopup {
    id: string;
    x: number;
    y: number;       // верхняя граница якоря (для попапа вверх)
    yBottom: number; // нижняя граница якоря (для попапа вниз)
    pinned: boolean;
}

interface PDFViewerProps {
    // Обязательный и БЕЗ дефолта - см. комментарий к ViewerMode в types/index.ts
    mode: ViewerMode;
    // Путь до воркера pdf.js. Дефолт '/pdf.worker.js' резолвится в корень origin;
    // приложениям, живущим под base path, надо передавать свой.
    workerSrc?: string;
    // Панель комментариев внедряется приложением (фильтрация и наличие
    // эндпоинта на запись различаются). null - не показывать.
    commentsPanel?: React.ComponentType<CommentsPanelProps> | null;
    // Права там, где бэкенд не отдаёт Actions[]. Ключ - Id замечания.
    contractorPermissions?: Record<number, ContractorNotePermissions>;
    onContractorComplete?: (noteId: number, comment: string) => Promise<void>;
    onContractorCancelCompletion?: (noteId: number) => Promise<void>;
    // Ссылка на карточку замечания; null - не рендерить.
    noteLinkHref?: (noteId: number) => string | null;
    url: string;
    initialHighlights?: Highlight[];
    onHighlightsChange?: (highlights: Highlight[]) => void | Promise<void>;
    onHighlightMove?: HighlightMoveHandler;
    workflowInstanceId?: number;
    noteObjectId?: number;
    workflowInstanceAttachmentId?: number;
    onLinkToExisting?: (geometry: HighlightGeometry, noteId: number) => Promise<void>;
    onDeleteNote?: (noteId: number) => Promise<void>;
    initialScrollToNoteId?: number;
    noteActions?: ModelAction[];
    onApproveNote?: (noteId: number) => Promise<void>;
    onCancelNote?: (noteId: number, comment: string) => Promise<void>;
    onReturnToExpert?: (noteId: number, comment: string) => Promise<void>;
    onSendForApproval?: (noteId: number) => Promise<void>;
    onRevokeDecision?: (noteId: number) => Promise<void>;
    onConfirmCompletion?: (noteId: number) => Promise<void>;
    onConfirmNotCompleted?: (noteId: number, comment: string) => Promise<void>;
    onCancelByInitiator?: (noteId: number, comment: string) => Promise<void>;
    onCancelCompletion?: (noteId: number) => Promise<void>;
    onCompleteCompletedGIP?: (noteId: number, comment: string) => Promise<void>;
    onRequestAssignToExpert?: (noteId: number) => void;
    workflowId?: number;
    onDownload?: () => void;
    /** Стартовать со свёрнутым сайдбаром замечаний (узкие split-view). */
    initialSidebarCollapsed?: boolean;
}

export function PDFViewer({ mode, workerSrc = DEFAULT_WORKER_SRC, commentsPanel, contractorPermissions, onContractorComplete, onContractorCancelCompletion, noteLinkHref, url, initialHighlights, onHighlightsChange, onHighlightMove, workflowInstanceId, noteObjectId, workflowInstanceAttachmentId, onLinkToExisting, onDeleteNote, initialScrollToNoteId, noteActions, onApproveNote, onCancelNote, onReturnToExpert, onSendForApproval, onRevokeDecision, onConfirmCompletion, onConfirmNotCompleted, onCancelByInitiator, onCancelCompletion, onCompleteCompletedGIP, onRequestAssignToExpert, workflowId, onDownload, initialSidebarCollapsed = false }: PDFViewerProps) {
    const canAnnotate = mode === 'edit';
    pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;
    //ширина и высота, с которыми работаем в пдф (открываем сам документ, отображаем текст, аннотации, подсветки...)
    const [dimension, setDimension] = useState({ width: 0, height: 0 });
    // Информация о всех подсветках, связанных с документом
    const [highlights, setHighlights] = useState<Highlight[]>([]);
    const [pdfError, setPdfError] = useState<string | null>(null);

    const onCommentsCountChange = useCallback((noteId: number, count: number) => {
        setHighlights(prev => prev.map(h =>
            h.detail?.noteId === noteId && h.detail
                ? { ...h, detail: { ...h.detail, commentsCount: count } }
                : h
        ));
    }, []);

    const [activePopup, setActivePopup] = useState<ActivePopup | null>(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [pdfReady, setPdfReady] = useState(false);
    const [isPageRendering, setIsPageRendering] = useState(false);
    const [pageInput, setPageInput] = useState('');
    const [pendingSelection, setPendingSelection] = useState<{
        startOffset: number;
        endOffset: number;
        text: string;
    } | null>(null);
    const [pendingComment, setPendingComment] = useState('');
    const [pendingIsCritical, setPendingIsCritical] = useState(false);
    const [pendingCodeId, setPendingCodeId] = useState<number | undefined>(undefined);
    const [editingHighlight, setEditingHighlight] = useState<{ id: string; note: string } | null>(null);
    const [editingComment, setEditingComment] = useState('');
    const [editingIsCritical, setEditingIsCritical] = useState(false);
    const [editingCodeId, setEditingCodeId] = useState<number | undefined>(undefined);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [pendingPolygon, setPendingPolygon] = useState<Array<{ x: number; y: number }> | null>(null);
    const [pendingRect, setPendingRect] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
    const [pendingMarker, setPendingMarker] = useState<{ x: number; y: number } | null>(null);
    // Link-to-existing flow
    const [showLinkScreen, setShowLinkScreen] = useState(false);
    const [linkedNote, setLinkedNote] = useState<{ id: number; title: string } | null>(null);
    // ref для актуального docId в stale-замыканиях (saveHighlights)
    const pdfDocIdRef = useRef(url.replace(/^\/|\.pdf$/g, ''));
    const initialHighlightsRef = useRef(initialHighlights);
    const onHighlightsChangeRef = useRef(onHighlightsChange);
    onHighlightsChangeRef.current = onHighlightsChange;
    const onHighlightMoveRef = useRef(onHighlightMove);
    onHighlightMoveRef.current = onHighlightMove;
    // Зеркало highlights — чтобы мутирующие колбэки считали новый массив вне updater'а
    // (вызов onSave внутри setState-updater'а запускал setState родителя в фазе рендера)
    const highlightsRef = useRef<Highlight[]>([]);
    highlightsRef.current = highlights;

    // Когда PDFViewerModal обновляет initialHighlights (например, после link-to-existing),
    // синхронизируем внутренний highlights-state чтобы не ждать перезагрузки страницы.
    // Несохранённые failed-заметки сохраняем, но не дублируем уже появившиеся с сервера.
    useEffect(() => {
        if (initialHighlights !== undefined) {
            initialHighlightsRef.current = initialHighlights;
            setHighlights((prev) => {
                const keepFailed = keepUnresolvedFailedHighlights(prev, initialHighlights);
                return keepFailed.length === 0
                    ? initialHighlights
                    : [...initialHighlights, ...keepFailed];
            });
        }
    }, [initialHighlights]);

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const textLayerRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const searchOverlayRef = useRef<HTMLDivElement>(null);
    const annotationLayerRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null); // внешняя обёртка (width:100%) — источник доступной ширины
    const pdfDocRef = useRef<any>(null); // кэш загруженного документа
    const renderTaskRef = useRef<any>(null); // текущий RenderTask — отменяем при быстром зуме/ресайзе
    const offscreenCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const offscreenCtxRef = useRef<CanvasRenderingContext2D | null>(null);
    const prevPageRef = useRef<number>(0);
    const flashElRef = useRef<HTMLElement[]>([]);
    // всегда актуальная restoreHighlights — renderPage вызывается из stale-замыкания
    const restoreHighlightsRef = useRef<() => void>(() => {});
    // отложенный scroll к замечанию (срабатывает после смены страницы + ре-рендера)
    const scrollToHighlightRef = useRef<string | null>(null);
    // авто-фокус на initialScrollToNoteId - срабатывает один раз после загрузки highlights
    const initialScrollDoneRef = useRef(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    // zoom при последнем реальном рендере canvas — база для промежуточного CSS scale
    const renderedZoomRef = useRef(1);
    const pdfLayersRef = useRef<HTMLDivElement>(null);
    // предыдущие параметры рендера — чтобы понять, изменился ли только zoom
    const prevRenderParamsRef = useRef({ pageNumber: 1, containerWidth: 0 });
    const [pageNumber, setPageNumber] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    // Панель миниатюр: по умолчанию закрыта, состояние помнится между открытиями
    // просмотрщика. Ленивая инициализация - localStorage не трогаем на каждый рендер.
    const [thumbsOpen, setThumbsOpen] = useState(() => {
        try {
            return localStorage.getItem('pdf-thumbs-open') === '1';
        } catch {
            return false;
        }
    });
    const toggleThumbs = useCallback(() => {
        setThumbsOpen((prev) => {
            const next = !prev;
            try {
                localStorage.setItem('pdf-thumbs-open', next ? '1' : '0');
            } catch {
                // приватный режим/квота - просто не запоминаем
            }
            return next;
        });
    }, []);

    const { zoom, setZoom, zoomInput, setZoomInput, applyZoomInput, zoomIn, zoomOut, zoomReset, zoomAnchorRef } = usePdfZoom({ scrollRef, pdfLayersRef, setActivePopup });

    const {
        placing, setPlacing,
        textMode, setTextMode,
        handMode, setHandMode,
        polygonMode, setPolygonMode,
        draftPoints, setDraftPoints,
        rectMode, setRectMode,
        rectDragging, setRectDragging,
        rectDraft, setRectDraft,
        freehandMode, setFreehandMode,
        freehandActive,
        moveMode, setMoveMode,
        freehandPolylineRef,
        freehandStartCircleRef,
        moveDragRef,
        resetFreehand,
        handleContainerClick,
        handleContainerDoubleClick,
        activeMode,
        activateMode,
        deactivateMode,
    } = useViewerModes({
        pdfLayersRef, overlayRef, dimension, pageNumber, pdfDocIdRef,
        onSaveRef: onHighlightsChangeRef,
        onHighlightMoveRef,
        highlightsRef,
        setHighlights, setActivePopup, setEditingHighlight,
        setPendingPolygon, setPendingComment, setPendingRect, setPendingMarker,
    });

    const { panRef, handlePanStart, handlePanMove, handlePanEnd } = usePdfPan({ scrollRef, handMode });

    const { sidebarRef, sidebarCollapsed, setSidebarCollapsed, sidebarWidth, handleDividerDown } = useSidebarResize({
        wrapperRef,
        setContainerWidth,
        initialSidebarCollapsed,
    });

    const { cancelClose, scheduleClose, openPopupForText, openPopupForPolygon, openPopupForRect, handleMarkerPopupEnter } = usePopupManager({ setActivePopup, overlayRef, dimension });

    const { codes, isExecutive, showCriticalCode } = useNoteCodes(workflowId);

    // Право двигать заметку = право её редактировать (действие Modify с бэкенда).
    // Нельзя опираться на «своя/чужая»: у ГИП есть Modify и на чужих замечаниях.
    const canMoveHighlight = useCallback((h: Highlight) => {
        // В review-режиме геометрия неприкосновенна, независимо от прав с бэкенда
        if (!canAnnotate) return false;
        const noteId = h.detail?.noteId;
        // Заметка только что создана и ещё не сохранена — действий по ней нет
        if (noteId == null) return true;
        // Список действий не передан (Word-вьювер) — откатываемся на признак «своя»
        if (!noteActions) return isOwnHighlight(h);
        return noteActions.some(a => a.Id === PDFNotesActionsVals.Modify && a.ObjectId === noteId);
    }, [noteActions, canAnnotate]);

    const [moveBlockedVisible, setMoveBlockedVisible] = useState(false);
    const moveBlockedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const showMoveBlocked = useCallback(() => {
        if (moveBlockedTimerRef.current) clearTimeout(moveBlockedTimerRef.current);
        setMoveBlockedVisible(true);
        moveBlockedTimerRef.current = setTimeout(() => setMoveBlockedVisible(false), 2000);
    }, []);

    const {
        updateHighlightNote, deleteHighlight, retryPersistHighlight,
        commitPendingSelection,
        commitPendingPolygon,
        commitPendingRect,
        commitPendingMarker,
        commitEditModal, cancelEditModal,
    } = useHighlightActions({
        onSaveRef: onHighlightsChangeRef, highlightsRef, pageNumber, setHighlights, setActivePopup,
        pendingSelection, setPendingSelection,
        pendingComment, setPendingComment,
        pendingIsCritical, setPendingIsCritical,
        pendingCodeId, setPendingCodeId,
        pendingPolygon, setPendingPolygon, deactivateMode,
        pendingRect, setPendingRect,
        pendingMarker, setPendingMarker,
        editingHighlight, setEditingHighlight,
        editingComment, setEditingComment,
        editingIsCritical, setEditingIsCritical,
        editingCodeId, setEditingCodeId,
    });

    const cancelPending = useCallback(() => {
        deactivateMode();
        setPendingSelection(null);
        setPendingPolygon(null);
        setPendingRect(null);
        setPendingMarker(null);
        setPendingComment('');
        setPendingIsCritical(false);
        setPendingCodeId(undefined);
        setLinkedNote(null);
    }, [deactivateMode]);

    // Открыть модал редактирования при клике на хайлайт
    const openEditModal = useCallback((h: Highlight) => {
        setActivePopup(null);
        cancelClose();
        setEditingHighlight({ id: h.id, note: h.note ?? '' });
        setEditingComment(h.note ?? '');
        setEditingIsCritical(h.isCritical ?? false);
        setEditingCodeId(h.codeId);
    }, [cancelClose]);

    // Промотать к замечанию и запустить flash-анимацию
    const scrollToId = useCallback((id: string) => {
        // Снять анимацию с предыдущих элементов
        flashElRef.current.forEach((p) => p.classList.remove('highlight--selected'));

        // Текстовая заметка рисуется несколькими дивами (по одному на строку) —
        // у всех одинаковый data-highlight-id, поэтому querySelectorAll
        const els = Array.from(
            document.querySelectorAll<HTMLElement>(
                `[data-highlight-id="${id}"], [data-marker-id="${id}"]`
            )
        );
        if (els.length === 0) return;

        // Порядок элементов в DOM - это порядок текстовых item'ов в PDF,
        // а не порядок чтения, поэтому els[0] может быть не самой верхней
        // строкой выделения. Скроллим к началу выделения — к верхнему боксу.
        const target = els.reduce((a, b) =>
            a.getBoundingClientRect().top <= b.getBoundingClientRect().top ? a : b
        );

        const cont = scrollRef.current;
        if (cont) {
            // Двигаем только скролл-контейнер PDF: scrollIntoView прокрутил бы
            // ещё и всех скроллящихся предков (модалку, страницу)
            const c = cont.getBoundingClientRect();
            const t = target.getBoundingClientRect();
            const TOP_MARGIN = 80;
            const top = Math.max(0, cont.scrollTop + (t.top - c.top) - TOP_MARGIN);
            // По горизонтали - только если выделение вне видимой области
            const left = t.left < c.left || t.right > c.right
                ? Math.max(0, cont.scrollLeft + (t.left - c.left) - 40)
                : cont.scrollLeft;
            cont.scrollTo({ top, left, behavior: 'smooth' });
        } else {
            target.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }

        els.forEach((el) => {
            el.classList.remove('highlight--selected');
            // reflow чтобы CSS-анимация перезапустилась при повторном клике
            void el.offsetWidth;
            el.classList.add('highlight--selected');
        });
        flashElRef.current = els;
        target.addEventListener('animationend', () => {
            els.forEach((el) => el.classList.remove('highlight--selected'));
            if (flashElRef.current === els) flashElRef.current = [];
        }, { once: true });
    }, []);

    const focusHighlight = useCallback((h: Highlight) => {
        setSelectedId(h.id);
        cancelClose();
        setActivePopup(null);
        if (h.pageNumber !== pageNumber) {
            scrollToHighlightRef.current = h.id;
            setPageNumber(h.pageNumber);
        } else {
            scrollToId(h.id);
        }
    }, [pageNumber, cancelClose, scrollToId]);

    // Авто-фокус на замечание при открытии (initialScrollToNoteId)
    useEffect(() => {
        if (!initialScrollToNoteId || initialScrollDoneRef.current) return;
        if (!pdfReady || highlights.length === 0) return;
        const h = highlights.find(hl => hl.id.startsWith(`${initialScrollToNoteId}-`));
        if (!h) return;
        initialScrollDoneRef.current = true;
        focusHighlight(h);
    }, [pdfReady, highlights, initialScrollToNoteId, focusHighlight]);

    // === Рендер страницы + text layer ===
    const renderPage = useCallback((page: any, scale: number, renderedZoom: number) => {
        const outputScale = window.devicePixelRatio || 1;
        const viewport = page.getViewport({ scale });
        const transform = outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null;

        // Переиспользуем offscreen canvas — создаём один раз, потом меняем размер
        let off = offscreenCanvasRef.current;
        let offCtx = offscreenCtxRef.current;
        if (!off) {
            off = document.createElement('canvas');
            offscreenCanvasRef.current = off;
            offCtx = off.getContext('2d')!;
            offscreenCtxRef.current = offCtx;
        }
        off.width = Math.floor(viewport.width * outputScale);
        off.height = Math.floor(viewport.height * outputScale);

        // Отменяем предыдущий рендер, чтобы pdfjs не ругался на параллельный рендер
        renderTaskRef.current?.cancel();
        const task = page.render({ canvasContext: offCtx!, transform, viewport });
        renderTaskRef.current = task;

        task.promise
            .then(() => {
                // Переносим готовую картинку в видимый canvas одним махом
                const canvas = canvasRef.current;
                if (!canvas) return;
                canvas.width = off.width;
                canvas.height = off.height;
                canvas.style.width = `${Math.floor(viewport.width)}px`;
                canvas.style.height = `${Math.floor(viewport.height)}px`;
                canvas.getContext('2d')!.drawImage(off, 0, 0);
                setIsPageRendering(false);

                // Синхронно (до отрисовки) приводим контейнер к новому размеру, убираем
                // промежуточный CSS scale и доводим скролл — картинка уже готова,
                // поэтому переход от размытой CSS-версии к чёткой без мигания.
                const layers = pdfLayersRef.current;
                const el = scrollRef.current;
                if (layers) {
                    layers.style.width = `${viewport.width}px`;
                    layers.style.height = `${viewport.height}px`;
                    layers.style.transform = '';
                    const anchor = zoomAnchorRef.current;
                    if (el && anchor) {
                        const rect = layers.getBoundingClientRect();
                        const curClientX = rect.left + anchor.fx * rect.width;
                        const curClientY = rect.top + anchor.fy * rect.height;
                        el.scrollLeft += curClientX - anchor.clientX;
                        el.scrollTop += curClientY - anchor.clientY;
                        zoomAnchorRef.current = null;
                    }
                }

                renderedZoomRef.current = renderedZoom;
                setDimension({ width: viewport.width, height: viewport.height });

                // Text Layer
                const textLayerDom = textLayerRef.current!;
                textLayerDom.innerHTML = '';
                textLayerDom.style.setProperty('--scale-factor', String(scale));
                page.getTextContent().then((content: any) => {
                    const tl = new (pdfjsLib as any).TextLayer({
                        container: textLayerDom,
                        textContentSource: content,
                        viewport
                    });
                    tl.render().then(() => {
                        restoreHighlightsRef.current();
                    });
                });

                // Annotation Layer — ссылки из PDF
                const annDiv = annotationLayerRef.current;
                if (annDiv) {
                    annDiv.innerHTML = '';
                    page.getAnnotations({ intent: 'display' }).then((annotations: any[]) => {
                        for (const ann of annotations) {
                            if (ann.subtype !== 'Link') continue;
                            if (!ann.url && !ann.dest && !ann.action) continue;

                            const [x1, y1] = (pdfjsLib as any).Util.applyTransform(
                                [ann.rect[0], ann.rect[1]], viewport.transform
                            );
                            const [x2, y2] = (pdfjsLib as any).Util.applyTransform(
                                [ann.rect[2], ann.rect[3]], viewport.transform
                            );
                            const left = Math.min(x1, x2);
                            const top = Math.min(y1, y2);
                            const width = Math.abs(x2 - x1);
                            const height = Math.abs(y2 - y1);

                            const a = document.createElement('a');
                            a.style.cssText = `position:absolute;left:${left}px;top:${top}px;width:${width}px;height:${height}px;cursor:pointer;`;

                            if (ann.url) {
                                a.href = ann.url;
                                a.target = '_blank';
                                a.rel = 'noopener noreferrer';
                            } else if (ann.dest) {
                                a.href = '#';
                                a.addEventListener('click', async (e) => {
                                    e.preventDefault();
                                    try {
                                        const dest = typeof ann.dest === 'string'
                                            ? await pdfDocRef.current?.getDestination(ann.dest)
                                            : ann.dest;
                                        if (!dest) return;
                                        const idx = await pdfDocRef.current?.getPageIndex(dest[0]);
                                        if (typeof idx === 'number') setPageNumber(idx + 1);
                                    } catch { /* игнорируем */ }
                                });
                            } else {
                                continue;
                            }

                            annDiv.appendChild(a);
                        }
                    });
                }
            })
            .catch(() => {
                /* RenderingCancelledException при отмене — игнорируем */
            });
    }, []);

    // Перерисовка оверлея текстовых заметок (позиционные рисует React)
    const restoreHighlights = useCallback(() => {
        const overlay = overlayRef.current;
        const textLayer = textLayerRef.current;
        if (!overlay || !textLayer) return;

        overlay.innerHTML = '';

        highlights
            .filter((h) => h.pageNumber === pageNumber && h.type === 'text')
            .forEach((h) => {
                renderHighlight(
                    h,
                    textLayer,
                    overlay,
                    (hl, el) => openPopupForText(hl, el), // hover — превью
                    scheduleClose,
                    (hl) => focusHighlight(hl) // click — подсветить в сайдбаре
                );
            });
        const pending = scrollToHighlightRef.current;
        if (pending) {
            scrollToHighlightRef.current = null;
            // небольшая задержка, чтобы overlay-дивы успели встать в DOM
            setTimeout(() => scrollToId(pending), 50);
        }
    }, [pageNumber, highlights, openPopupForText, focusHighlight, scheduleClose, scrollToId]);

    // === Создание текстовой заметки из выделения ===
    const handleMouseUp = useCallback(() => {
        if (!canAnnotate) return;
        if (placing) return;
        if (!textMode) return;

        const selection = window.getSelection();
        if (!selection || selection.isCollapsed) return;

        const textLayer = textLayerRef.current!;
        if (!textLayer.contains(selection.anchorNode)) return;

        const range = selection.getRangeAt(0);
        const { startOffset, endOffset, text } = serializeSelection(textLayer, range);

        if (!text.trim() || startOffset < 0 || endOffset < 0) return;

        selection.removeAllRanges();
        setPendingSelection({ startOffset, endOffset, text });
        setPendingComment('');
    }, [placing, textMode, canAnnotate]);



    // Загрузка заметок и PDF при смене документа
    useEffect(() => {
        const docId = url.replace(/^\//, '').replace(/\.pdf$/, '');
        pdfDocIdRef.current = docId;
        initialScrollDoneRef.current = false;
        setHighlights([]);
        setSelectedId(null);
        setActivePopup(null);
        if (initialHighlightsRef.current !== undefined) {
            setHighlights(initialHighlightsRef.current);
        }
    }, [url]);

    useEffect(() => {
        // Гонка: при быстрой смене url устаревший getDocument мог долететь
        // последним и перезаписать pdfDocRef/pdfReady документом от старого url.
        let cancelled = false;

        setPdfReady(false);
        setPdfError(null);
        setPageNumber(1);
        setTotalPages(0);

        // Корректное уничтожение старого документа — освобождает парсированные потоки, шрифты, изображения
        const oldDoc = pdfDocRef.current;
        pdfDocRef.current = null;
        prevPageRef.current = 0;

        if (oldDoc) {
            oldDoc.destroy().catch(() => {});
        }

        // Отмена текущего рендера
        renderTaskRef.current?.cancel();
        renderTaskRef.current = null;

        pdfjsLib
            .getDocument({ url })
            .promise.then((pdf) => {
                if (cancelled) {
                    pdf.destroy().catch(() => {});
                    return;
                }
                pdfDocRef.current = pdf;
                setTotalPages(pdf.numPages);
                setPdfReady(true);
            })
            .catch((e) => {
                console.error('pdf.js load error', e);
                if (!cancelled) setPdfError('Не удалось открыть документ');
            });

        return () => { cancelled = true; };
    }, [url]);

    // Cleanup всех ресурсов при размонтировании компонента
    useEffect(() => {
        return () => {
            renderTaskRef.current?.cancel();
            renderTaskRef.current = null;
            if (pdfDocRef.current) {
                pdfDocRef.current.destroy().catch(() => {});
                pdfDocRef.current = null;
            }
            // Обнуляем canvas — браузер немедленно освобождает GPU-буфер
            const canvas = canvasRef.current;
            if (canvas) { canvas.width = 0; canvas.height = 0; }
            const off = offscreenCanvasRef.current;
            if (off) { off.width = 0; off.height = 0; }
        };
    }, []);

    // Рендер текущей страницы при смене страницы / зума / доступной ширины
    useEffect(() => {
        const pdf = pdfDocRef.current;
        if (!pdfReady || !pdf || containerWidth <= 0) return;

        const prev = prevRenderParamsRef.current;
        const zoomOnly = prev.pageNumber === pageNumber && prev.containerWidth === containerWidth;
        prevRenderParamsRef.current = { pageNumber, containerWidth };

        // Во время дебаунса — сразу масштабируем слой через CSS вокруг точки-якоря,
        // чтобы точка под курсором визуально оставалась на месте до реального ре-рендера
        if (zoomOnly && pdfLayersRef.current && renderedZoomRef.current > 0) {
            const anchor = zoomAnchorRef.current;
            const cssScale = zoom / renderedZoomRef.current;
            if (anchor) {
                pdfLayersRef.current.style.transformOrigin = `${anchor.fx * 100}% ${anchor.fy * 100}%`;
            }
            pdfLayersRef.current.style.transform = `scale(${cssScale})`;
        }

        if (!zoomOnly) {
            setIsPageRendering(true);
            // Cleanup предыдущей страницы — освобождает промежуточные данные парсинга
            if (prev.pageNumber > 0 && prev.pageNumber !== pageNumber) {
                pdf.getPage(prev.pageNumber).then((p: any) => p.cleanup()).catch(() => {});
            }
        }

        let cancelled = false;
        const doRender = () => {
            pdf.getPage(pageNumber).then((page: any) => {
                if (cancelled) return;
                const nativeWidth = page.getViewport({ scale: 1 }).width;
                // Доступная ширина: clientWidth зоны .scroll минус её padding (иначе
                // страница чуть шире области и появляется горизонтальный скролл).
                const scrollEl = scrollRef.current;
                let availableWidth = containerWidth - 4;
                if (scrollEl) {
                    const cs = getComputedStyle(scrollEl);
                    const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
                    availableWidth = Math.max(0, scrollEl.clientWidth - padX - 4);
                }
                const fitScale = availableWidth / nativeWidth;
                const baseScale = Math.min(MAX_BASE, fitScale);
                const renderScale = Math.max(0.1, baseScale * zoom);
                renderPage(page, renderScale, zoom);
            });
        };

        // Зум-only: дебаунс 200мс — ждём паузы между тиками колеса, рендерим один раз
        const delay = zoomOnly ? 200 : 0;
        const timer = setTimeout(doRender, delay);
        return () => { cancelled = true; clearTimeout(timer); setIsPageRendering(false); };
    }, [pageNumber, zoom, containerWidth, pdfReady, renderPage]);

    // Перерисовка текстовых оверлеев при изменении заметок/страницы (без перезагрузки PDF)
    useEffect(() => {
        restoreHighlightsRef.current = restoreHighlights;
        if (textLayerRef.current?.childNodes.length) {
            restoreHighlights();
        }
    }, [highlights, restoreHighlights]);

    // Пиксельные координаты попапа устаревают при смене масштаба/ширины — закрываем
    useEffect(() => {
        setActivePopup(null);
    }, [containerWidth]);

    const applyPageInput = () => {
        const val = parseInt(pageInput, 10);
        if (!isNaN(val) && val >= 1 && val <= totalPages) {
            setActivePopup(null);
            setPageNumber(val);
        }
        setPageInput('');
    };

    function onPrevPage() {
        if (pageNumber - 1 < 1) return;
        setActivePopup(null);
        setPageNumber(pageNumber - 1);
    }

    function onNextPage() {
        if (pageNumber + 1 > totalPages) return;
        setActivePopup(null);
        setPageNumber(pageNumber + 1);
    }

    const activeHighlight = activePopup
        ? highlights.find((h) => h.id === activePopup.id)
        : undefined;

    const pendingAnnotation = useMemo(() => {
        if (!canAnnotate) return null;
        if (showLinkScreen) return null;
        if (pendingSelection) return { type: 'text' as const, title: 'Добавить замечание', quote: pendingSelection.text, geometry: { type: 'text' as const, page: pageNumber, ...pendingSelection } as HighlightGeometry, commit: commitPendingSelection };
        if (pendingPolygon) return { type: 'polygon' as const, title: 'Добавить замечание к фигуре', geometry: { type: 'polygon' as const, page: pageNumber, points: pendingPolygon } as HighlightGeometry, commit: commitPendingPolygon };
        if (pendingRect) return { type: 'rect' as const, title: 'Добавить замечание к прямоугольнику', geometry: { type: 'rect' as const, page: pageNumber, ...pendingRect } as HighlightGeometry, commit: commitPendingRect };
        if (pendingMarker) return { type: 'positioned' as const, title: 'Добавить замечание к метке', geometry: { type: 'positioned' as const, page: pageNumber, x: pendingMarker.x, y: pendingMarker.y } as HighlightGeometry, commit: commitPendingMarker };
        return null;
    }, [canAnnotate, showLinkScreen, pendingSelection, pendingPolygon, pendingRect, pendingMarker, pageNumber, commitPendingSelection, commitPendingPolygon, commitPendingRect, commitPendingMarker]);

    const { thumbs, aspect: thumbAspect, itemRef: thumbItemRef } = usePdfThumbnails({
        pdfDocRef,
        totalPages,
        enabled: thumbsOpen && pdfReady,
        fallbackDocId: workflowInstanceAttachmentId,
        currentPage: pageNumber,
    });
    const onSelectThumb = useCallback((page: number) => {
        setPageNumber(page);
    }, []);

    const documentSearch = usePdfViewerSearch({
        pdfDocRef,
        pdfReady,
        pageNumber,
        setPageNumber,
        textLayerRef,
        searchOverlayRef,
        scrollRef,
        isPageRendering,
    });

    useEffect(() => {
        documentSearch.setQuery('');
    }, [url]);

    return (
        <div className={styles.viewer}>
            {thumbsOpen && pdfReady && (
                <ThumbnailsSidebar
                    totalPages={totalPages}
                    currentPage={pageNumber}
                    thumbs={thumbs}
                    aspect={thumbAspect}
                    itemRef={thumbItemRef}
                    onSelectPage={onSelectThumb}
                />
            )}
            <div className={styles.pdfColumn} ref={wrapperRef}>
            <PdfToolbar
                mode={mode}
                thumbsOpen={thumbsOpen}
                onToggleThumbs={toggleThumbs}
                pageNumber={pageNumber}
                pageInput={pageInput}
                onPageInputChange={setPageInput}
                onPageInputBlur={applyPageInput}
                totalPages={totalPages}
                onPrevPage={onPrevPage}
                onNextPage={onNextPage}
                zoom={zoom}
                zoomInput={zoomInput}
                onZoomInputChange={setZoomInput}
                onZoomInputBlur={applyZoomInput}
                zoomIn={zoomIn}
                zoomOut={zoomOut}
                zoomReset={zoomReset}
                zoomMin={ZOOM_MIN}
                zoomMax={ZOOM_MAX}
                activeMode={activeMode}
                activateMode={activateMode}
                polygonPointsCount={draftPoints.length}
                freehandActive={freehandActive}
                trailing={(
                    <DocumentSearchBar
                        query={documentSearch.query}
                        onQueryChange={documentSearch.setQuery}
                        matchLabel={documentSearch.matchLabel}
                        matchCount={documentSearch.matchCount}
                        onPrev={documentSearch.goPrev}
                        onNext={documentSearch.goNext}
                        searching={documentSearch.searching}
                        inputRef={documentSearch.searchInputRef}
                    />
                )}
            />
            <div
                className={styles.scroll}
                ref={scrollRef}
                onMouseMove={handlePanMove}
                onMouseUp={handlePanEnd}
                onMouseLeave={handlePanEnd}
            >
                <div
                    ref={pdfLayersRef}
                    className={styles['pdf-layers']}
                    style={{
                        width: `${dimension.width}px`,
                        height: `${dimension.height}px`,
                        cursor: handMode ? (panRef.current ? 'grabbing' : 'grab') : moveMode ? (moveDragRef.current ? 'grabbing' : 'move') : (placing || polygonMode || freehandMode || rectMode) ? 'crosshair' : undefined
                    }}
                    onMouseDown={(e) => {
                        if (rectMode && dimension.width && dimension.height) {
                            const base = pdfLayersRef.current!.getBoundingClientRect();
                            const { nx, ny } = toNormalized(e.clientX, e.clientY, base, dimension.width, dimension.height);
                            setRectDragging(true);
                            setRectDraft({ x1: nx, y1: ny, x2: nx, y2: ny });
                            return;
                        }
                        handlePanStart(e);
                    }}
                    onMouseUp={handMode ? handlePanEnd : handleMouseUp}
                    onClick={handMode ? undefined : handleContainerClick}
                    onDoubleClick={handleContainerDoubleClick}
                >
                <canvas ref={canvasRef} className={styles['pdf-layer__canvas']} />
                {pdfReady && isPageRendering && (
                    <div className={styles.pageLoadingOverlay}>
                        <i className={`pi pi-spinner pi-spin ${styles.pageLoadingSpinner}`} />
                        Загрузка…
                    </div>
                )}
                <div ref={textLayerRef} className={clsx(styles['pdf-layer__text'], {[styles['pdf-layer__text--noSelect']]: activeMode && activeMode !== 'text'})} />
                <div ref={searchOverlayRef} className={styles['pdf-layer__search']} />
                {/* Слой подсветок текстовых заметок — императивные дивы */}
                <div ref={overlayRef} className={styles['pdf-layer__overlay']} />
                {/* Слой ссылок PDF — positioned <a> поверх всего */}
                <div ref={annotationLayerRef} className={styles['pdf-layer__annotations']} />

                <SvgAnnotationLayer
                    highlights={highlights}
                    pageNumber={pageNumber}
                    dimension={dimension}
                    polygonMode={polygonMode}
                    moveMode={moveMode}
                    moveDragRef={moveDragRef}
                    pdfLayersRef={pdfLayersRef}
                    draftPoints={draftPoints}
                    freehandPolylineRef={freehandPolylineRef}
                    freehandStartCircleRef={freehandStartCircleRef}
                    rectDraft={rectDraft}
                    onPopupForPolygon={openPopupForPolygon}
                    onPopupForRect={openPopupForRect}
                    onScheduleClose={scheduleClose}
                    onOpenEditModal={focusHighlight}
                    onMoveBlocked={showMoveBlocked}
                    canMove={canMoveHighlight}
                />

                <PositionedMarkers
                    highlights={highlights}
                    pageNumber={pageNumber}
                    dimension={dimension}
                    moveMode={moveMode}
                    moveDragRef={moveDragRef}
                    pdfLayersRef={pdfLayersRef}
                    onPopupEnter={handleMarkerPopupEnter}
                    onScheduleClose={scheduleClose}
                    onOpenEditModal={focusHighlight}
                    onMoveBlocked={showMoveBlocked}
                    canMove={canMoveHighlight}
                />

                {/* Попап заметки — только при pinned (создание/редактирование), hover-превью скрыт */}
                {activePopup && activePopup.pinned && activeHighlight && (
                    <NotePopup
                        key={activePopup.id}
                        note={activeHighlight.note || ''}
                        x={activePopup.x}
                        y={activePopup.y}
                        yBottom={activePopup.yBottom}
                        autoFocus={activePopup.pinned}
                        onChange={(note) => updateHighlightNote(activePopup.id, note)}
                        onDelete={() => deleteHighlight(activePopup.id)}
                        onClose={() => setActivePopup(null)}
                        onFocus={() => {
                            cancelClose();
                            setActivePopup((prev) => (prev ? { ...prev, pinned: true } : prev));
                        }}
                        onMouseEnter={cancelClose}
                        onMouseLeave={scheduleClose}
                    />
                )}
                </div>
            </div>
                {canAnnotate && <PendingAnnotationModals
                    editingHighlight={editingHighlight}
                    editingComment={editingComment}
                    onEditingCommentChange={setEditingComment}
                    onCommitEdit={commitEditModal}
                    onCancelEdit={cancelEditModal}
                    onDelete={deleteHighlight}
                    editingIsCritical={editingIsCritical}
                    onEditingIsCriticalChange={setEditingIsCritical}
                    editingCodeId={editingCodeId}
                    onEditingCodeIdChange={setEditingCodeId}
                    showLinkScreen={showLinkScreen}
                    linkedNote={linkedNote}
                    onLinkedNoteChange={setLinkedNote}
                    onShowLinkScreenChange={setShowLinkScreen}
                    workflowInstanceId={workflowInstanceId}
                    noteObjectId={noteObjectId}
                    workflowInstanceAttachmentId={workflowInstanceAttachmentId}
                    onLinkToExisting={onLinkToExisting}
                    pendingAnnotation={pendingAnnotation}
                    pendingComment={pendingComment}
                    onPendingCommentChange={setPendingComment}
                    pendingIsCritical={pendingIsCritical}
                    onPendingIsCriticalChange={setPendingIsCritical}
                    pendingCodeId={pendingCodeId}
                    onPendingCodeIdChange={setPendingCodeId}
                    onCancelPending={cancelPending}
                    codes={codes}
                    isExecutive={isExecutive}
                    showCriticalCode={showCriticalCode}
                />}

                {!pdfReady && (
                    <div className={styles.openingOverlay}>
                        {pdfError ? (
                            <>
                                <span className={styles.openingError}>{pdfError}</span>
                                {onDownload && (
                                    <button type="button" className={styles.openingErrorButton} onClick={onDownload}>
                                        Скачать документ
                                    </button>
                                )}
                            </>
                        ) : (
                            <>
                                <i className={`pi pi-spinner pi-spin ${styles.openingSpinner}`} />
                                Открытие документа…
                            </>
                        )}
                    </div>
                )}

            </div>{/* .pdfColumn */}

            {!sidebarCollapsed && (
                <div
                    className={styles.divider}
                    onMouseDown={handleDividerDown}
                />
            )}

            <AnnotationsSidebar
                ref={sidebarRef}
                mode={mode}
                commentsPanel={commentsPanel}
                contractorPermissions={contractorPermissions}
                onContractorComplete={onContractorComplete}
                onContractorCancelCompletion={onContractorCancelCompletion}
                noteLinkHref={noteLinkHref}
                onCommentsCountChange={onCommentsCountChange}
                highlights={highlights}
                selectedId={selectedId}
                currentPage={pageNumber}
                collapsed={sidebarCollapsed}
                onSelect={focusHighlight}
                onSaveNote={updateHighlightNote}
                onDelete={deleteHighlight}
                onRetryPersist={retryPersistHighlight}
                onDeleteNote={onDeleteNote}
                onToggle={() => setSidebarCollapsed((v) => !v)}
                style={sidebarCollapsed ? undefined : { width: sidebarWidth }}
                noteActions={noteActions}
                onApproveNote={onApproveNote}
                onCancelNote={onCancelNote}
                onReturnToExpert={onReturnToExpert}
                onSendForApproval={onSendForApproval}
                onRevokeDecision={onRevokeDecision}
                onConfirmCompletion={onConfirmCompletion}
                onConfirmNotCompleted={onConfirmNotCompleted}
                onCancelByInitiator={onCancelByInitiator}
                onCancelCompletion={onCancelCompletion}
                onCompleteCompletedGIP={onCompleteCompletedGIP}
                onRequestAssignToExpert={onRequestAssignToExpert}
                codes={codes}
                isExecutive={isExecutive}
                showCriticalCode={showCriticalCode}
            />

            {/* Тост поверх модалки — fixed относительно окна, а не контейнера просмотрщика */}
            {moveBlockedVisible && (
                <div style={{
                    position: 'fixed',
                    top: 16,
                    right: 16,
                    background: 'rgba(30,30,30,0.92)',
                    color: '#fff',
                    padding: '10px 16px',
                    borderRadius: 6,
                    fontSize: 13,
                    pointerEvents: 'none',
                    zIndex: 100000,
                    maxWidth: 320,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                }}>
                    Недостаточно прав для редактирования
                </div>
            )}
        </div>
    );
}
